import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Shield, Lock, CheckCircle, AlertTriangle, Eye, EyeOff } from 'lucide-react';

const API_BASE = 'https://warngen-api-352514293915.europe-west1.run.app';


export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token') || '';
    const emailParam = searchParams.get('email') || '';

    const [email, setEmail] = useState(emailParam);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [mostrarPassword, setMostrarPassword] = useState(false);
    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState('');
    const [exito, setExito] = useState(false);

    const validateComplexity = (pwd) => {
        return {
            length: pwd.length >= 8,
            upper: /[A-Z]/.test(pwd),
            lower: /[a-z]/.test(pwd),
            number: /[0-9]/.test(pwd),
        };
    };

    const rules = validateComplexity(newPassword);
    const isValid = rules.length && rules.upper && rules.lower && rules.number && newPassword === confirmPassword;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (newPassword !== confirmPassword) {
            setError('Las contraseñas no coinciden');
            return;
        }

        if (!rules.length || !rules.upper || !rules.lower || !rules.number) {
            setError('La contraseña no cumple los requisitos de seguridad del ENS.');
            return;
        }

        setCargando(true);
        try {
            const res = await fetch(`${API_BASE}/api/auth/reset-password/confirm`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, token, newPassword })
            });
            const data = await res.json();
            if (data.success) {
                setExito(true);
            } else {
                setError(data.error || 'Error al actualizar la contraseña');
            }
        } catch (e) {
            setError('Error de conexión con el servidor.');
        }
        setCargando(false);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-600 to-primary-800 dark:from-gray-900 dark:to-gray-950 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl p-8 w-full max-w-md">
                <div className="text-center mb-6">
                    <div className="inline-block p-3 bg-primary-100 dark:bg-primary-900 rounded-full mb-3">
                        <Shield className="text-primary-600 dark:text-primary-400" size={36} />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Nueva Contraseña</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Establece tu nueva credencial de acceso seguro
                    </p>
                </div>

                {exito ? (
                    <div className="text-center space-y-4 py-4">
                        <CheckCircle size={54} className="text-green-500 mx-auto" />
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">¡Contraseña Actualizada!</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                            Tu contraseña ha sido modificada con éxito conforme a las directivas del ENS.
                        </p>
                        <button
                            onClick={() => navigate('/')}
                            className="w-full mt-4 bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 font-semibold transition"
                        >
                            Ir al Inicio de Sesión
                        </button>
                    </div>
                ) : !token ? (
                    <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-start gap-2">
                        <AlertTriangle size={20} className="shrink-0 mt-0.5" />
                        <div>
                            <p className="font-semibold">Enlace no válido</p>
                            <p className="mt-1">Falta el token de seguridad. Solicita un nuevo enlace desde la pantalla de login.</p>
                            <button onClick={() => navigate('/')} className="mt-3 text-primary-600 font-semibold underline">
                                Volver al login
                            </button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full px-4 py-2 border rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nueva Contraseña</label>
                            <div className="relative">
                                <input
                                    type={mostrarPassword ? "text" : "password"}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="w-full px-4 py-2 pr-10 border rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm"
                                    placeholder="••••••••"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setMostrarPassword(!mostrarPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                                >
                                    {mostrarPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confirmar Contraseña</label>
                            <input
                                type={mostrarPassword ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full px-4 py-2 border rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm"
                                placeholder="••••••••"
                                required
                            />
                        </div>

                        {/* Indicadores de complejidad ENS */}
                        <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-xs space-y-1.5 border dark:border-gray-600">
                            <p className="font-semibold text-gray-700 dark:text-gray-300">Requisitos de seguridad (ENS):</p>
                            <p className={rules.length ? 'text-green-600 dark:text-green-400 font-medium' : 'text-gray-400'}>
                                • Mínimo 8 caracteres
                            </p>
                            <p className={rules.upper ? 'text-green-600 dark:text-green-400 font-medium' : 'text-gray-400'}>
                                • Al menos una letra mayúscula
                            </p>
                            <p className={rules.lower ? 'text-green-600 dark:text-green-400 font-medium' : 'text-gray-400'}>
                                • Al menos una letra minúscula
                            </p>
                            <p className={rules.number ? 'text-green-600 dark:text-green-400 font-medium' : 'text-gray-400'}>
                                • Al menos un número
                            </p>
                        </div>

                        {error && (
                            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={!isValid || cargando}
                            className="w-full bg-primary-600 text-white py-3 rounded-lg hover:bg-primary-700 font-semibold transition disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            <Lock size={18} />
                            {cargando ? 'Guardando...' : 'Cambiar Contraseña'}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}